/**
 * WC-V20-B — vendor chat activity → Layer A bell bridge (read-only informational rows).
 */
export const CHAT_ACTIVITY_BRIDGE_KIND = "chat_activity_bridge";

const REF_PREFIX = "[chat_bridge_ref:";
const MSG_PREFIX = "[chat_bridge_msg:";
const CHAN_PREFIX = "[chat_bridge_chan:";

export type ParsedChatBridgeRef = {
  channelId: string;
  hourBucket: string;
};

export function utcHourBucketIso(nowMs: number = Date.now()): string {
  const d = new Date(nowMs);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const h = String(d.getUTCHours()).padStart(2, "0");
  return `${y}-${mo}-${day}T${h}`;
}

export function chatBridgeRefMarker(channelId: string, hourBucket: string): string {
  return `${REF_PREFIX}${channelId.trim()}:${hourBucket.trim()}]`;
}

export function chatBridgeMessageMarker(messageId: string): string {
  return `${MSG_PREFIX}${messageId.trim()}]`;
}

export function chatBridgeChannelMarker(channelId: string, channelSlug: string): string {
  return `${CHAN_PREFIX}${channelId.trim()}:${channelSlug.trim()}]`;
}

const REF_REGEX = /\[chat_bridge_ref:([^:\]]+):([^\]]+)\]/;
const MSG_REGEX = /\[chat_bridge_msg:([^\]]+)\]/;
const CHAN_REGEX = /\[chat_bridge_chan:([^:]+):([^\]]+)\]/;

export function parseChatBridgeRefFromBody(body: string): ParsedChatBridgeRef | null {
  const m = REF_REGEX.exec(String(body || ""));
  if (!m?.[1] || !m?.[2]) return null;
  return { channelId: m[1].trim(), hourBucket: m[2].trim() };
}

export function parseChatBridgeMessageIdFromBody(body: string): string | null {
  const m = MSG_REGEX.exec(String(body || ""));
  return m?.[1]?.trim() || null;
}

export function channelLabelFromSlug(slug: string): string {
  const s = String(slug || "").trim() || "general";
  return s.startsWith("#") ? s : `#${s}`;
}

export function buildChatBridgeTitle(channelSlug: string): string {
  return `Activity in ${channelLabelFromSlug(channelSlug)}`;
}

export function buildChatBridgeBody(input: {
  channelId: string;
  channelSlug: string;
  hourBucket: string;
  messageId: string;
  preview: string;
  senderName?: string | null;
}): string {
  const ref = chatBridgeRefMarker(input.channelId, input.hourBucket);
  const msg = chatBridgeMessageMarker(input.messageId);
  const chan = chatBridgeChannelMarker(input.channelId, input.channelSlug);
  const who = input.senderName?.trim() ? `${input.senderName}: ` : "";
  const preview = String(input.preview || "").trim().slice(0, 500) || "(new message)";
  return `${ref}\n${msg}\n${chan}\n${who}${preview}\nOpen Team chat tab to reply.`.trim();
}

/** Deep-link hint for client (channel id + slug embedded in body markers). */
export function parseChatBridgeChannelFromBody(body: string): { channelId: string; channelSlug: string } | null {
  const m = CHAN_REGEX.exec(String(body || ""));
  if (!m?.[1]) return null;
  return { channelId: m[1].trim(), channelSlug: (m[2] || "general").trim() };
}

/** Client/server shared org team channel id (matches Stream adapter). */
export function orgTeamChannelId(orgId: string, slug: string): string {
  const safe = slug.replace(/[^a-z0-9_-]/gi, "").slice(0, 24) || "general";
  return `org-${orgId.slice(0, 8)}-${safe}`;
}
